import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { apiClient } from "../../api/client";
import { OverflowMenu } from "../common/OverflowMenu";
import {
  projectsQueryKeys,
  useLayers,
  useRuns,
  useSitePlanCandidates,
} from "../../hooks/useProjects";
import type {
  Layer,
  Run,
  SitePlanCandidate,
  Study,
  UserBrief,
} from "../../types/projects";
import { AddStudyModal } from "./AddStudyModal";
import { DeleteRunModal } from "./DeleteRunModal";
import { DeleteStudyModal } from "./DeleteStudyModal";

interface Props {
  projectId: string;
  studies: Study[];
}

// Workflow stepper — only steps with `available: true` are rendered.
// Later-stage stubs (Site Plan, Design Rules, Generate, Review Layout,
// Export) are kept here for plan visibility but hidden from the UI.
const STEPS: { key: string; label: string; available: boolean }[] = [
  { key: "site-plan", label: "Site Plan", available: false },
  { key: "components", label: "Components", available: false },
  { key: "design-rules", label: "Design Rules", available: false },
  { key: "generate", label: "Generate", available: false },
  { key: "review-layout", label: "Review Layout", available: false },
  { key: "export", label: "Export", available: false },
];
const VISIBLE_STEPS = STEPS.filter((s) => s.available);

function formatUser(user: UserBrief | null, fallbackId: string): string {
  if (!user) return fallbackId;
  const fullName = [user.given_name, user.family_name]
    .filter(Boolean)
    .join(" ");
  return fullName || user.email;
}

// Supported GCR range (matches backend validation 0 < gcr <= 1, narrowed
// to the physically sensible utility-scale window).
const GCR_MIN = 0.2;
const GCR_MAX = 0.9;

/** The GCR a run was packed with, read from its input_snapshot. Null on
 * older runs that predate the gcr field. */
function runGcr(run: Run): number | null {
  const v = (run.input_snapshot as { gcr?: unknown } | null)?.gcr;
  return typeof v === "number" ? v : null;
}

/** Number of trackers (racks) placed in a run's layout. */
function runRackCount(run: Run): number | null {
  const trackers = (run.output_data as { trackers?: unknown } | null)
    ?.trackers;
  return Array.isArray(trackers) ? trackers.length : null;
}

function formatGcr(gcr: number | null): string {
  return gcr === null ? "—" : gcr.toFixed(2);
}

function formatCount(n: number | null): string {
  return n === null ? "—" : n.toLocaleString();
}

/** A source file (layer) that can act as a site plan: at least one
 * boundary feature, plus however many exclusions / roads came with it. */
export interface SitePlanFile {
  layerId: string;
  filename: string;
  boundaries: number;
  exclusions: number;
  roads: number;
}

/** Group the candidate pool by source file, keeping only files that have
 * at least one boundary (you can't build a layout without a boundary).
 * This is what lets the operator pick "the KMZ" instead of an opaque
 * per-feature candidate like "KML_1". */
export function groupSitePlanFiles(
  candidates: SitePlanCandidate[] | undefined,
  layers: Layer[] | undefined,
): SitePlanFile[] {
  const byLayer = new Map<string, SitePlanFile>();
  for (const c of candidates ?? []) {
    let file = byLayer.get(c.layer_id);
    if (!file) {
      const layer = layers?.find((l) => l.id === c.layer_id);
      file = {
        layerId: c.layer_id,
        filename: layer?.original_filename || layer?.name || c.layer_id,
        boundaries: 0,
        exclusions: 0,
        roads: 0,
      };
      byLayer.set(c.layer_id, file);
    }
    if (c.candidate_type === "boundary") file.boundaries += 1;
    else if (c.candidate_type === "exclusion") file.exclusions += 1;
    else if (c.candidate_type === "road") file.roads += 1;
  }
  return [...byLayer.values()].filter((f) => f.boundaries > 0);
}

/** Dropdown label: filename + a short summary of what the file will
 * contribute (build area vs. no-build zones). */
export function sitePlanFileLabel(f: SitePlanFile): string {
  const boundary =
    f.boundaries === 1 ? "1 boundary" : `${f.boundaries} boundaries`;
  const parts = [boundary];
  if (f.exclusions)
    parts.push(
      f.exclusions === 1 ? "1 exclusion" : `${f.exclusions} exclusions`,
    );
  if (f.roads) parts.push(f.roads === 1 ? "1 road" : `${f.roads} roads`);
  return `${f.filename} — ${parts.join(", ")}`;
}

export function ProjectStudiesTab({ projectId, studies }: Props) {
  const [showAdd, setShowAdd] = useState(false);

  return (
    <section data-testid="studies-tab">
      <WorkflowStepper projectId={projectId} />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "1rem",
        }}
      >
        <h2 style={sectionHeadingStyle}>Studies</h2>
        <button
          type="button"
          data-testid="add-study-trigger"
          onClick={() => setShowAdd(true)}
          style={primaryBtnStyle}
        >
          + Add Study
        </button>
      </div>

      <p style={explanationStyle}>
        <em>
          Generating a run packs the chosen site plan at the chosen GCR and
          opens it in the layout viewer. Generate several runs at different
          GCRs to compare densities — one run per GCR (duplicates are
          blocked).
        </em>
      </p>

      {studies.length === 0 ? (
        <p>No studies yet.</p>
      ) : (
        studies.map((s) => (
          <StudyCard key={s.id} projectId={projectId} study={s} />
        ))
      )}

      {showAdd && (
        <AddStudyModal
          projectId={projectId}
          onClose={() => setShowAdd(false)}
        />
      )}
    </section>
  );
}

function WorkflowStepper({ projectId }: { projectId: string }) {
  if (VISIBLE_STEPS.length === 0) return null;
  return (
    <nav
      data-testid="workflow-stepper"
      aria-label="Workflow steps"
      style={{
        display: "flex",
        gap: "0.5rem",
        marginBottom: "1rem",
        flexWrap: "wrap",
      }}
    >
      {VISIBLE_STEPS.map((step) => {
        const content = (
          <div
            data-testid={`workflow-step-${step.key}`}
            style={{
              padding: "0.5rem 0.75rem",
              borderRadius: 6,
              border: "1px solid #d1d5db",
              background: "white",
              color: "#111827",
              fontSize: "0.85rem",
            }}
          >
            {step.label}
          </div>
        );
        if (step.key === "components") {
          return (
            <Link
              key={step.key}
              to={`/projects/${projectId}/components?type=modules`}
              style={{ textDecoration: "none" }}
            >
              {content}
            </Link>
          );
        }
        return <span key={step.key}>{content}</span>;
      })}
    </nav>
  );
}

function StudyCard({
  projectId,
  study,
}: {
  projectId: string;
  study: Study;
}) {
  const { data: runs } = useRuns(study.id);
  const qc = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredRunId, setHoveredRunId] = useState<string | null>(null);
  const [pendingDeleteRun, setPendingDeleteRun] = useState<Run | null>(null);
  const [showDeleteStudy, setShowDeleteStudy] = useState(false);

  // Site-plan files the user can pack this study against. The operator
  // picks a whole file (KMZ): its boundary defines the build area, its
  // roads + exclusions become no-build zones. Defaults to the first file
  // until the user picks another.
  const { data: candidates } = useSitePlanCandidates(projectId);
  const { data: layers } = useLayers(projectId);
  const siteFiles = useMemo(
    () => groupSitePlanFiles(candidates, layers),
    [candidates, layers],
  );
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const effectiveLayerId =
    selectedLayerId ?? siteFiles[0]?.layerId ?? null;

  // Ground Coverage Ratio — controls row pitch. Editable per run so the
  // operator can compare densities. Stored as a string so the input can
  // be cleared while typing; parsed + clamped on generate.
  const [gcrInput, setGcrInput] = useState("0.30");

  const canGenerate =
    study.workflow_type === "array_layout" ||
    study.workflow_type === "ac_block_layout";

  async function handleAddRun() {
    setIsGenerating(true);
    setError(null);
    try {
      // Clamp GCR into the supported 0.20–0.90 range; fall back to the
      // 0.30 baseline if the field was left blank or unparseable.
      const parsedGcr = Number.parseFloat(gcrInput);
      const gcr = Number.isFinite(parsedGcr)
        ? Math.min(GCR_MAX, Math.max(GCR_MIN, parsedGcr))
        : 0.3;
      // A study can hold many runs — one per GCR, to compare densities —
      // but not two at the SAME GCR (that would be a duplicate).
      const duplicate = (runs ?? []).some((r) => {
        const g = runGcr(r);
        return g !== null && Math.abs(g - gcr) < 0.005;
      });
      if (duplicate) {
        setError(
          `This study already has a run at GCR ${gcr.toFixed(2)}. ` +
            "Change the GCR to generate a different layout.",
        );
        setIsGenerating(false);
        return;
      }
      // Target THIS study explicitly — without study_id the route falls back
      // to get_or_create_default and always picks the project's oldest study.
      const res = await apiClient.post<{ run_id: string }>(
        `/api/projects/${projectId}/layout/generate`,
        {
          study_id: study.id,
          site_plan_layer_id: effectiveLayerId ?? undefined,
          gcr,
        },
      );
      void qc.invalidateQueries({ queryKey: projectsQueryKeys.runs(study.id) });
      // FULL page load (not SPA navigate): the ODA viewer's WASM engine is
      // a page-level singleton that accumulates/corrupts state across runs.
      // Loading the viewer as a fresh page guarantees a clean engine every
      // time — the old page unloads and frees the WASM heap.
      window.location.assign(
        `/projects/${projectId}/layout/${res.data.run_id}`,
      );
    } catch (err: unknown) {
      let msg: string;
      if (axios.isAxiosError(err)) {
        const detail = (err.response?.data as { detail?: string } | undefined)
          ?.detail;
        msg = detail ?? err.message;
      } else if (err instanceof Error) {
        msg = err.message;
      } else {
        msg = String(err);
      }
      setError(msg);
      setIsGenerating(false);
    }
  }

  function handleDeleteRun(run: Run) {
    setPendingDeleteRun(run);
  }

  return (
    <div data-testid={`study-${study.id}`} style={sectionCardStyle}>
      <header
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        <div>
          <h3 style={studyTitleStyle}>{study.name}</h3>
          <div style={studyMetaStyle}>
            {study.workflow_type} · {study.is_active ? "active" : "inactive"}
          </div>
        </div>
        <div
          style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
        >
          {canGenerate && (
            <button
              type="button"
              onClick={handleAddRun}
              disabled={isGenerating}
              data-testid={`add-run-${study.id}`}
              style={{
                ...primaryBtnStyle,
                cursor: isGenerating ? "default" : "pointer",
                opacity: isGenerating ? 0.7 : 1,
              }}
            >
              {isGenerating ? "⏳ Generating..." : "+ Generate Run"}
            </button>
          )}
          <OverflowMenu
            triggerTestId={`study-overflow-trigger-${study.id}`}
            ariaLabel={`More actions for ${study.name}`}
            items={[
              {
                label: "Delete study",
                destructive: true,
                onSelect: () => setShowDeleteStudy(true),
                "data-testid": `study-overflow-delete-${study.id}`,
              },
            ]}
          />
        </div>
      </header>

      {canGenerate && siteFiles.length > 0 && (
        <div style={boundaryRowStyle}>
          <label
            htmlFor={`site-plan-select-${study.id}`}
            style={boundaryLabelStyle}
          >
            Site plan file:
          </label>
          <select
            id={`site-plan-select-${study.id}`}
            data-testid={`site-plan-select-${study.id}`}
            value={effectiveLayerId ?? ""}
            onChange={(e) => setSelectedLayerId(e.target.value)}
            disabled={isGenerating}
            style={selectStyle}
          >
            {siteFiles.map((f) => (
              <option key={f.layerId} value={f.layerId}>
                {sitePlanFileLabel(f)}
              </option>
            ))}
          </select>

          <label htmlFor={`gcr-input-${study.id}`} style={boundaryLabelStyle}>
            GCR:
          </label>
          <input
            id={`gcr-input-${study.id}`}
            data-testid={`gcr-input-${study.id}`}
            type="number"
            min={GCR_MIN}
            max={GCR_MAX}
            step={0.01}
            value={gcrInput}
            onChange={(e) => setGcrInput(e.target.value)}
            disabled={isGenerating}
            style={gcrInputStyle}
            title="Ground Coverage Ratio — controls row spacing (pitch = tracker width / GCR). Range 0.20–0.90; lower = more spread out, higher = denser."
          />
          <span style={gcrHintStyle}>
            0.20–0.90 — lower is sparser, higher is denser
          </span>
        </div>
      )}

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      <h4 style={subHeadingStyle}>Runs</h4>
      {runs && runs.length === 0 && (
        <p style={{ margin: 0, color: "#666" }}>No runs yet.</p>
      )}
      {runs && runs.length > 0 && (
        <table
          data-testid={`runs-table-${study.id}`}
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead>
            <tr>
              <th style={cellStyle}>#</th>
              <th style={cellStyle}>Status</th>
              <th style={cellStyle}>GCR</th>
              <th style={cellStyle}>Racks</th>
              <th style={cellStyle}>Triggered by</th>
              <th style={cellStyle}>Created</th>
              <th style={{ ...cellStyle, width: 36 }} aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => {
              const isHovered = hoveredRunId === r.id;
              return (
                <tr
                  key={r.id}
                  data-testid={`run-row-${r.id}`}
                  onClick={() =>
                    // Full page load → fresh ODA engine (see handleAddRun).
                    window.location.assign(
                      `/projects/${projectId}/layout/${r.id}`,
                    )
                  }
                  onMouseEnter={() => setHoveredRunId(r.id)}
                  onMouseLeave={() =>
                    setHoveredRunId((id) => (id === r.id ? null : id))
                  }
                  style={{
                    cursor: "pointer",
                    background: isHovered
                      ? "rgba(99, 91, 255, 0.04)"
                      : "transparent",
                    transition: "background-color 80ms ease",
                  }}
                  title="Open in layout viewer"
                >
                  <td style={cellStyle}>{r.run_number}</td>
                  <td style={cellStyle}>{r.status}</td>
                  <td style={cellStyle}>{formatGcr(runGcr(r))}</td>
                  <td style={cellStyle}>{formatCount(runRackCount(r))}</td>
                  <td style={cellStyle}>
                    {formatUser(r.triggered_by_user, r.triggered_by)}
                  </td>
                  <td style={cellStyle}>
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td
                    style={{
                      ...cellStyle,
                      width: 36,
                      textAlign: "center",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isHovered && (
                      <button
                        type="button"
                        aria-label={`Delete run #${r.run_number}`}
                        data-testid={`delete-run-${r.id}`}
                        onClick={() => handleDeleteRun(r)}
                        style={trashBtnStyle}
                        title="Delete run"
                      >
                        🗑
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {pendingDeleteRun && (
        <DeleteRunModal
          runId={pendingDeleteRun.id}
          runNumber={pendingDeleteRun.run_number}
          studyId={study.id}
          onClose={() => setPendingDeleteRun(null)}
          onDeleted={() => setPendingDeleteRun(null)}
        />
      )}

      {showDeleteStudy && (
        <DeleteStudyModal
          projectId={projectId}
          studyId={study.id}
          studyName={study.name}
          runCount={runs?.length}
          onClose={() => setShowDeleteStudy(false)}
          onDeleted={() => setShowDeleteStudy(false)}
        />
      )}
    </div>
  );
}

/* ----- styling tokens (parallel to ProjectSummaryTab + ProjectFilesTab) ----- */

const sectionCardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #E3E8EE",
  borderRadius: 12,
  padding: "1.5rem 1.75rem",
  marginBottom: "1rem",
};

const sectionHeadingStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "1.05rem",
  fontWeight: 600,
  color: "#0a0e27",
  letterSpacing: "-0.005em",
};

const subHeadingStyle: React.CSSProperties = {
  margin: "1.25rem 0 0.5rem",
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "#0a0e27",
};

const studyTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "1.05rem",
  fontWeight: 600,
  color: "#0a0e27",
};

const studyMetaStyle: React.CSSProperties = {
  marginTop: "0.15rem",
  color: "#666",
  fontSize: "0.875rem",
};

const primaryBtnStyle: React.CSSProperties = {
  background: "#635bff",
  color: "white",
  border: "none",
  padding: "0.55rem 1rem",
  borderRadius: 6,
  cursor: "pointer",
  fontWeight: 500,
  whiteSpace: "nowrap",
};

const explanationStyle: React.CSSProperties = {
  color: "#666",
  fontSize: "0.875rem",
  marginTop: 0,
  marginBottom: "1rem",
};

const errorStyle: React.CSSProperties = {
  color: "#dc2626",
  marginTop: "0.5rem",
  fontSize: "0.875rem",
};

const boundaryRowStyle: React.CSSProperties = {
  marginTop: "0.85rem",
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  flexWrap: "wrap",
};

const boundaryLabelStyle: React.CSSProperties = {
  fontSize: "0.85rem",
  color: "#475569",
  fontWeight: 500,
};

const selectStyle: React.CSSProperties = {
  padding: "0.4rem 0.6rem",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  background: "white",
  fontSize: "0.85rem",
  color: "#111827",
  maxWidth: 360,
};

const gcrInputStyle: React.CSSProperties = {
  width: 72,
  padding: "0.4rem 0.5rem",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  background: "white",
  fontSize: "0.85rem",
  color: "#111827",
};

const gcrHintStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "#94a3b8",
};

const cellStyle: React.CSSProperties = {
  border: "1px solid #E3E8EE",
  padding: "0.5rem",
  textAlign: "left",
};

const trashBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 4,
  borderRadius: 4,
  cursor: "pointer",
  fontSize: "1rem",
  lineHeight: 1,
  color: "#94a3b8",
};
